import {
  CallScribeParticipantStatus,
  CallScribeSpeaker,
  ICallScribeParticipant,
} from 'services/callScribe/types';

export const SPEAKER_LABEL: Record<CallScribeSpeaker, string> = {
  [CallScribeSpeaker.Technician]: 'Technician',
  [CallScribeSpeaker.Customer]: 'Customer',
};

export const normaliseSpeaker = (value: unknown): CallScribeSpeaker => {
  if (value === CallScribeSpeaker.Technician || value === CallScribeSpeaker.Customer) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    if (trimmed === 'customer' || trimmed === '1') return CallScribeSpeaker.Customer;
    if (trimmed === 'technician' || trimmed === '0') return CallScribeSpeaker.Technician;
  }
  return CallScribeSpeaker.Technician;
};

export const participantKey = (seg: {
  participantId?: number | null;
  speaker: CallScribeSpeaker;
}): string =>
  typeof seg.participantId === 'number'
    ? `p:${seg.participantId}`
    : `s:${normaliseSpeaker(seg.speaker)}`;

export const findParticipant = (
  seg: { participantId?: number | null; speaker?: unknown },
  participants?: ICallScribeParticipant[],
): ICallScribeParticipant | undefined => {
  if (typeof seg.participantId === 'number') {
    return participants?.find(p => p.id === seg.participantId);
  }
  if (seg.speaker === undefined) return undefined;
  const role = normaliseSpeaker(seg.speaker);
  const inRole = (participants ?? []).filter(p => normaliseSpeaker(p.role) === role);
  return inRole.length === 1 ? inRole[0] : undefined;
};

export const isNamed = (p: ICallScribeParticipant): boolean =>
  Boolean(p.label?.trim()) && p.nameSource !== 0 && p.nameSource !== 'None';

export const customerDisplayName = (
  participants: ICallScribeParticipant[] | undefined,
  fallback: string,
): string => {
  const customers = (participants ?? []).filter(
    p => normaliseSpeaker(p.role) === CallScribeSpeaker.Customer,
  );
  const primary = customers.find(p => p.isPrimary) ?? customers[0];
  return primary && isNamed(primary) ? primary.label.trim() : fallback;
};

export const participantLabel = (
  seg: { participantId?: number | null; speaker: CallScribeSpeaker },
  participants?: ICallScribeParticipant[],
): string => {
  const label = findParticipant(seg, participants)?.label?.trim();
  return label || SPEAKER_LABEL[normaliseSpeaker(seg.speaker)];
};

export const OTHER_LANE_LABEL = 'Technician and others';

export const laneLabel = (
  role: CallScribeSpeaker,
  participants: ICallScribeParticipant[] | undefined,
): string => {
  const everyone = participants ?? [];
  const inRole = everyone.filter(p => normaliseSpeaker(p.role) === role);
  if (everyone.length > 2) {
    if (role === CallScribeSpeaker.Technician) return OTHER_LANE_LABEL;
    const primary = inRole.find(p => p.isPrimary) ?? inRole[0];
    return primary?.label?.trim() || SPEAKER_LABEL[role];
  }
  const label = inRole.length === 1 ? inRole[0].label?.trim() : '';
  return label || SPEAKER_LABEL[role];
};

export const PARTICIPANT_STATUS_TEXT: Record<CallScribeParticipantStatus, string> = {
  [CallScribeParticipantStatus.Ringing]: 'Calling…',
  [CallScribeParticipantStatus.Joined]: 'On the call',
  [CallScribeParticipantStatus.Left]: 'Left',
  [CallScribeParticipantStatus.Declined]: 'Declined',
  [CallScribeParticipantStatus.NoAnswer]: 'No answer',
  [CallScribeParticipantStatus.Busy]: 'Busy',
  [CallScribeParticipantStatus.Failed]: "Couldn't connect",
};

export const normaliseParticipantStatus = (value: unknown): CallScribeParticipantStatus => {
  if (typeof value === 'number' && value in PARTICIPANT_STATUS_TEXT) {
    return value as CallScribeParticipantStatus;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase();
    const numeric = Number(trimmed);
    if (trimmed !== '' && Number.isInteger(numeric) && numeric in PARTICIPANT_STATUS_TEXT) {
      return numeric as CallScribeParticipantStatus;
    }
    const byName = Object.entries(CallScribeParticipantStatus).find(
      ([name, v]) => typeof v === 'number' && name.toLowerCase() === trimmed,
    );
    if (byName) return byName[1] as CallScribeParticipantStatus;
  }
  return CallScribeParticipantStatus.Joined;
};

export const isActiveStatus = (status: CallScribeParticipantStatus): boolean =>
  status === CallScribeParticipantStatus.Ringing || status === CallScribeParticipantStatus.Joined;

export const isOwnTechnician = (p: ICallScribeParticipant): boolean =>
  p.isPrimary && normaliseSpeaker(p.role) === CallScribeSpeaker.Technician;
